import { useState, useEffect, useRef } from 'react';
import { rgpdApi, documentsApi } from '../api';
import type { RGPDAlert, AlertsSummary } from '../types';
import { useSettings } from '../context/SettingsContext';
import {
  AlertTriangle, CheckCircle, XCircle, RefreshCw, Bell, Trash2,
  FileText, User, CalendarClock, UploadCloud,
} from 'lucide-react';
import { Header, StatsCard, SEVERITY_STYLES } from '../components/ui';

export function RGPDPage() {
  const { formatDate } = useSettings();
  const [alerts, setAlerts] = useState<RGPDAlert[]>([]);
  const [summary, setSummary] = useState<AlertsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [replacingAlertId, setReplacingAlertId] = useState<string | null>(null);
  const [deleteChoiceAlert, setDeleteChoiceAlert] = useState<RGPDAlert | null>(null);
  const [scanSourceAlert, setScanSourceAlert] = useState<RGPDAlert | null>(null);
  const [postponeAlert, setPostponeAlert] = useState<RGPDAlert | null>(null);
  const [postponeDays, setPostponeDays] = useState<number>(30);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const PAGE_SIZE = 20;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const pendingAlertRef = useRef<{ alertId: string; collaborateurId?: string } | null>(null);

  const loadAlerts = async () => {
    const params: Record<string, any> = {
      page: currentPage,
      size: PAGE_SIZE,
      status: 'active',
    };
    if (activeTab !== 'all') params.severity = activeTab;
    setLoading(true);
    const [aRes, sRes] = await Promise.all([
      rgpdApi.listAlerts(params),
      rgpdApi.summary(),
    ]);
    setAlerts(aRes.data.items);
    setTotalPages(aRes.data.pages);
    setSummary(sRes.data);
    setLoading(false);
  };

  useEffect(() => { void loadAlerts(); }, [currentPage, activeTab]);

  const changeTab = (key: string) => {
    setActiveTab(key);
    setCurrentPage(1);
  };

  const handleAction = (
    alertId: string,
    action: string,
    postponeDays?: number,
    deleteDocument?: boolean,
    deleteScanSource?: boolean,
  ) => {
    rgpdApi.action(alertId, action, postponeDays, deleteDocument, deleteScanSource).then(() => {
      if (['acknowledge', 'resolve', 'postpone', 'replace', 'delete'].includes(action)) {
        setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      }
      void loadAlerts();
      window.dispatchEvent(new Event('acodoc:refresh-sidebar'));
    });
  };

  // Étape 1 : l'utilisateur choisit de supprimer le document ou juste l'alerte
  const handleDeleteChoice = (deleteDocument: boolean) => {
    if (!deleteChoiceAlert) return;
    if (deleteDocument && deleteChoiceAlert.document?.source?.startsWith('scan:')) {
      // Étape 2 : doc scanné → demander si on supprime aussi le fichier source
      setScanSourceAlert(deleteChoiceAlert);
      setDeleteChoiceAlert(null);
    } else {
      handleAction(deleteChoiceAlert.id, 'delete', undefined, deleteDocument, false);
      setDeleteChoiceAlert(null);
    }
  };

  // Étape 2 (suite) : réponse sur le fichier source
  const handleScanSourceChoice = (deleteScanSource: boolean) => {
    if (!scanSourceAlert) return;
    handleAction(scanSourceAlert.id, 'delete', undefined, true, deleteScanSource);
    setScanSourceAlert(null);
  };

  const handleReplace = (alert: RGPDAlert) => {
    pendingAlertRef.current = {
      alertId: alert.id,
      collaborateurId: alert.document?.collaborateur_id,
    };
    fileInputRef.current?.click();
  };

  const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const pending = pendingAlertRef.current;
    if (!file || !pending) return;

    setReplacingAlertId(pending.alertId);
    documentsApi.upload(file, pending.collaborateurId)
      .then(() => rgpdApi.action(pending.alertId, 'replace'))
      .then(() => {
        loadAlerts();
        window.dispatchEvent(new Event('acodoc:refresh-sidebar'));
      })
      .finally(() => {
        setReplacingAlertId(null);
        pendingAlertRef.current = null;
        if (fileInputRef.current) fileInputRef.current.value = '';
      });
  };

  const criticalCount = summary?.critical ?? 0;
  const warningCount = summary?.urgent ?? 0;
  const infoCount = summary?.warning ?? 0;
  const urgentTotal = criticalCount + warningCount;
  const totalActiveAlerts =
    criticalCount + (summary?.urgent ?? 0) + infoCount + (summary?.info ?? 0);

  return (
    <div className="flex-1 overflow-y-auto">
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.jpg,.jpeg,.png,.tiff"
        className="hidden"
        onChange={handleFileSelected}
      />
      <Header title=" Alertes RGPD" />
      <div className="p-6 space-y-6">
        {urgentTotal > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <p className="font-bold text-red-800">{urgentTotal} alerte{urgentTotal > 1 ? 's' : ''} nécessitent votre attention</p>
                <p className="text-sm text-red-600 hidden sm:block">Des documents arrivent à expiration ou dépassent leur durée de conservation légale.</p>
              </div>
            </div>
            <button
              onClick={() => {
                changeTab(criticalCount > 0 ? 'critical' : 'urgent');
                document.getElementById('rgpd-alerts-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg text-sm font-semibold shrink-0 transition"
            >
              Traiter maintenant
            </button>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { key: 'critical', label: 'Critiques (expirés)', value: criticalCount,           icon: XCircle,      iconBg: 'bg-red-50',     iconColor: 'text-red-600',     accent: 'text-red-600' },
            { key: 'urgent',   label: 'Urgents (7j)',        value: summary?.urgent ?? '—',  icon: AlertTriangle, iconBg: 'bg-amber-50',  iconColor: 'text-amber-600',  accent: 'text-amber-600' },
            { key: 'warning',  label: 'Avert. (30j)',        value: infoCount,                icon: Bell,         iconBg: 'bg-sky-50',     iconColor: 'text-sky-600',     accent: 'text-sky-600' },
            { key: 'all',      label: 'Conformes',           value: summary?.conformes ?? '—',icon: CheckCircle,  iconBg: 'bg-emerald-50', iconColor: 'text-emerald-600', accent: 'text-emerald-600' },
          ].map(card => (
            <button
              key={card.key}
              onClick={() => {
                if (card.key !== 'all') {
                  changeTab(card.key);
                  document.getElementById('rgpd-alerts-list')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
              }}
              className={`text-left w-full rounded-xl ${card.key !== 'all' ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gray-200 transition' : 'cursor-default'} ${activeTab === card.key ? 'ring-2 ring-offset-1 ring-teal-400' : ''}`}
            >
              <StatsCard label={card.label} value={card.value} icon={card.icon} iconBg={card.iconBg} iconColor={card.iconColor} accent={card.accent} />
            </button>
          ))}
        </div>

        <div id="rgpd-alerts-list">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <h2 className="text-lg font-bold text-gray-900">Alertes actives</h2>
            <div className="flex flex-wrap gap-1.5">
              {[
                { key: 'all',      label: `Toutes (${totalActiveAlerts})` },
                { key: 'critical', label: `Critiques (${criticalCount})` },
                { key: 'urgent',   label: `Urgents (${summary?.urgent ?? 0})` },
                { key: 'warning',  label: `Avert. (${infoCount})` },
              ].map((t) => (
                <button
                  key={t.key}
                  onClick={() => changeTab(t.key)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition ${activeTab === t.key ? 'bg-[#0c1929] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <RefreshCw className="w-8 h-8 text-gray-300 animate-spin" />
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-16 bg-emerald-50 rounded-xl border border-emerald-200">
              <CheckCircle className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
              <p className="text-emerald-700 font-semibold">Aucune alerte active</p>
              <p className="text-sm text-emerald-600 mt-1">Tous les documents sont conformes.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((a) => {
                const ss = SEVERITY_STYLES[a.severity] || SEVERITY_STYLES.info;
                const docName = a.document?.original_filename ?? 'Document inconnu';
                const docType = a.document?.document_type?.label ?? '—';
                const collab = a.document?.collaborateur
                  ? a.document.collaborateur.nom_complet
                  : '—';
                const dueDate = a.due_date
                  ? formatDate(a.due_date)
                  : '—';
                const SeverityIcon = ss.Icon;
                return (
                  <div key={a.id} className={`${ss.bg} rounded-xl border-l-4 ${ss.border} p-4 sm:p-5`}>
                    <div className="flex flex-wrap sm:flex-nowrap items-start justify-between gap-3">
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <SeverityIcon className={`w-5 h-5 ${ss.icon} mt-0.5 shrink-0`} />
                        <div className="flex-1 min-w-0">
                          <p className={`font-bold ${ss.text} truncate`}>{docName}</p>
                          <p className={`text-sm mt-1 ${ss.text} opacity-80`}>{a.message}</p>
                          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                            <span className="flex items-center gap-1"><FileText className="w-3.5 h-3.5" />{docType}</span>
                            <span className="flex items-center gap-1"><User className="w-3.5 h-3.5" />{collab}</span>
                            <span className="flex items-center gap-1"><CalendarClock className="w-3.5 h-3.5" />{dueDate}</span>
                          </div>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 mt-2 sm:mt-0 sm:ml-4 justify-end">
                        {a.severity === 'critical' && (
                          <button
                            onClick={() => setDeleteChoiceAlert(a)}
                            className="bg-red-600 hover:bg-red-700 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                          >
                            <Trash2 className="w-3 h-3" /> Supprimer
                          </button>
                        )}
                        <button
                          onClick={() => handleReplace(a)}
                          disabled={replacingAlertId === a.id}
                          className="bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1"
                        >
                          {replacingAlertId === a.id
                            ? <><RefreshCw className="w-3 h-3 animate-spin" /> Upload...</>
                            : <><UploadCloud className="w-3 h-3" /> Remplacer</>
                          }
                        </button>
                        <button
                          onClick={() => { setPostponeAlert(a); setPostponeDays(30); }}
                          className="bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-gray-50"
                        >
                          Reporter
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-1 py-4">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                ←
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce<(number | 'ellipsis')[]>((acc, p, idx, arr) => {
                  if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('ellipsis');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === 'ellipsis' ? (
                    <span key={`e-${idx}`} className="px-2 text-gray-400 text-sm">…</span>
                  ) : (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p as number)}
                      className={`w-8 h-8 text-sm rounded-lg border transition ${
                        currentPage === p
                          ? 'bg-teal-600 text-white border-teal-600 font-semibold'
                          : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  )
                )}
              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
              >
                →
              </button>
            </div>
          )}
        </div>
      </div>
      {/* ── Modal Reporter ──────────────────────────────────────── */}
      {postponeAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-sm rounded-xl bg-white shadow-2xl border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Reporter l'alerte</h3>
              <p className="text-sm text-gray-600 mt-1">
                Dans combien de jours souhaitez-vous que cette alerte réapparaisse ?
              </p>
              <p className="text-xs text-gray-400 mt-1 truncate">
                {postponeAlert.document?.original_filename ?? 'Document inconnu'}
              </p>
            </div>
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-3">
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={postponeDays}
                  onChange={e => setPostponeDays(Math.max(1, Math.min(365, Number(e.target.value))))}
                  className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm text-center font-semibold focus:outline-none focus:ring-2 focus:ring-teal-400"
                />
                <span className="text-sm text-gray-600">jour{postponeDays > 1 ? 's' : ''}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {[7, 14, 30, 60, 90].map(d => (
                  <button
                    key={d}
                    onClick={() => setPostponeDays(d)}
                    className={`px-3 py-1 rounded-lg text-xs font-medium border transition ${postponeDays === d ? 'bg-teal-600 text-white border-teal-600' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                  >
                    {d}j
                  </button>
                ))}
              </div>
              <div className="flex gap-3 pt-2 border-t border-gray-100">
                <button
                  onClick={() => {
                    handleAction(postponeAlert.id, 'postpone', postponeDays);
                    setPostponeAlert(null);
                  }}
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold transition"
                >
                  Confirmer le report
                </button>
                <button
                  onClick={() => setPostponeAlert(null)}
                  className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal 1 : Supprimer doc+alerte ou alerte seule ────── */}
      {deleteChoiceAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-xl rounded-xl bg-white shadow-2xl border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Suppression RGPD</h3>
              <p className="text-sm text-gray-600 mt-1">
                Choisissez si vous souhaitez supprimer totalement le document, ou conserver le document
                et supprimer seulement l&apos;alerte.
              </p>
              <p className="text-xs text-gray-500 mt-2 truncate">
                Document : {deleteChoiceAlert.document?.original_filename ?? 'Document inconnu'}
              </p>
            </div>
            <div className="p-5 space-y-3">
              <button
                onClick={() => handleDeleteChoice(true)}
                className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
              >
                Supprimer le document et l&apos;alerte
              </button>
              <button
                onClick={() => handleDeleteChoice(false)}
                className="w-full bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Garder le document, supprimer seulement l&apos;alerte
              </button>
              <button
                onClick={() => setDeleteChoiceAlert(null)}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-1"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal 2 : Supprimer le fichier source (scan uniquement) */}
      {scanSourceAlert && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="w-full max-w-lg rounded-xl bg-white shadow-2xl border border-gray-100">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold text-gray-900">Supprimer le fichier source ?</h3>
              <p className="text-sm text-gray-600 mt-1">
                Ce document a été importé depuis un répertoire de scan.
                Souhaitez-vous également supprimer le fichier original dans ce répertoire ?
              </p>
              <p className="text-xs text-gray-500 mt-2 truncate">
                Document : {scanSourceAlert.document?.original_filename ?? 'Document inconnu'}
              </p>
              {scanSourceAlert.document?.source && (
                <p className="text-xs text-gray-400 mt-1">
                  Source : {scanSourceAlert.document.source.replace('scan:', '')}
                </p>
              )}
            </div>
            <div className="p-5 space-y-3">
              <button
                onClick={() => handleScanSourceChoice(true)}
                className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-lg text-sm font-semibold"
              >
                Oui, supprimer aussi le fichier source
              </button>
              <button
                onClick={() => handleScanSourceChoice(false)}
                className="w-full bg-white border border-gray-200 text-gray-700 px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50"
              >
                Non, conserver le fichier dans le répertoire source
              </button>
              <button
                onClick={() => setScanSourceAlert(null)}
                className="w-full text-sm text-gray-500 hover:text-gray-700 py-1"
              >
                Annuler
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
